import React, { useState } from 'react';
import { ChevronRight, Check, Sparkles, Crown, Shield, Lock, User, Bell, Trash2, AlertTriangle, Save, Mail, Settings } from 'lucide-react';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { ChangeEmailModal } from '../components/Settings/ChangeEmailModal';
import { TwoFactorModal } from '../components/Settings/TwoFactorModal';
import { PrivacySettingsModal } from '../components/Settings/PrivacySettingsModal';
import { SubscriptionModal } from '../components/Settings/SubscriptionModal';
import { DeleteAccountModal } from '../components/Settings/DeleteAccountModal';
import { BillingManagementModal } from '../components/Settings/BillingManagementModal';
import { CancelSubscriptionModal } from '../components/Settings/CancelSubscriptionModal';
import { deleteAccount } from '../services/accountService';
import { supabase } from '../lib/supabaseClient';
import { usePlanEntitlement } from '../hooks/usePlanEntitlement';
export function SettingsPage() {
  const navigate = useNavigate();
  const {
    user,
    signOut,
    updateUserProfile
  } = useAuth();
  const {
    showToast
  } = useToast();
  const [formData, setFormData] = useState({
    firstName: user?.user_metadata?.firstName || '',
    lastName: user?.user_metadata?.lastName || '',
    email: user?.email || ''
  });
  const [emailNotifications, setEmailNotifications] = useState(user?.user_metadata?.emailNotifications ?? true);
  const [pushNotifications, setPushNotifications] = useState(user?.user_metadata?.pushNotifications ?? true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // The user's real plan and scan entitlement — nothing here is hardcoded.
  const {
    planId,
    planLabel,
    priceLabel,
    billingLabel,
    accessLabel,
    statusLabel,
    unlimited,
    renewalDate,
    loading: loadingProfile
  } = usePlanEntitlement();
  const handleSaveChanges = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await updateUserProfile({
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      setSuccess('Profile updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  const handleNotificationChange = async (type: 'email' | 'push', value: boolean) => {
    setError(null);
    setSuccess(null);
    if (type === 'email') {
      setEmailNotifications(value);
    } else {
      setPushNotifications(value);
    }
    try {
      await updateUserProfile({
        emailNotifications: type === 'email' ? value : emailNotifications,
        pushNotifications: type === 'push' ? value : pushNotifications
      });
      setSuccess(`${type === 'email' ? 'Email' : 'Push'} notifications ${value ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      if (type === 'email') {
        setEmailNotifications(!value);
      } else {
        setPushNotifications(!value);
      }
      setError(`Failed to update ${type} notification settings`);
    }
  };
  const handleLogout = async () => {
    await signOut();
    navigate('/');
    showToast('Logged out successfully', 'success');
  };
  const openDeleteAccount = () => {
    setDeleteError(null);
    setShowDeleteAccountModal(true);
  };
  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteError(null);
    // Deletion happens server-side only; success requires `success: true`.
    const result = await deleteAccount();
    if (!result.success) {
      setDeleteError(result.error);
      setDeleteLoading(false);
      return;
    }
    try {
      await supabase.auth.signOut();
    } catch {
      /* the account no longer exists — local cleanup below still runs */
    }
    try {
      window.localStorage.clear();
    } catch {
      /* storage unavailable */
    }
    try {
      window.sessionStorage.clear();
    } catch {
      /* storage unavailable */
    }
    setDeleteLoading(false);
    setShowDeleteAccountModal(false);
    navigate('/', {
      replace: true
    });
    showToast('Your account has been deleted.', 'success', 5000);
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-9858" data-mp="29ytdtiuegicf">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-9859" data-mp="2evthxo7ovb0w" />

      <WebsiteHeader data-id="element-9860" data-mp="1r8ts95xrpldr" />

      <div className="max-w-3xl mx-auto w-full relative z-10" data-id="element-9861" data-mp="1r9dayov8sdsu">
        <main className="px-6 pt-12 pb-24" data-id="element-9862" data-mp="1w7omr553kydp">
          {/* Page Header */}
          <div className="mb-12" data-id="element-9863" data-mp="1h9mdcebdxioj">
            <div className="inline-flex items-center gap-2 mb-4 bg-[#FF0000]/10 border border-[#FF0000]/30 px-3 py-1" data-id="element-9864" data-mp="1hanqqpfvih9f">
              <Settings size={14} className="text-[#FF0000]" data-id="element-9865" data-mp="1m7nxl2kjro1l" />
              <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-9866" data-mp="1342ptlq0hbul">
                System Configuration
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-2 uppercase tracking-tight" data-id="element-9867" data-mp="tdrgfjnk6agb">
              Settings & Preferences
            </h1>
            <p className="text-gray-400 font-mono text-sm max-w-xl" data-id="element-9868" data-mp="ograd8qpl7p7">
              Manage your account parameters, security protocols, and
              notification preferences.
            </p>
          </div>

          {/* Success/Error Messages */}
          {success && <div className="bg-green-500/10 border border-green-500/30 p-4 mb-8 flex items-center gap-3" data-id="element-9869" data-mp="1c37joeqmoh0f">
              <Check className="text-green-500 flex-shrink-0" size={20} data-id="element-9870" data-mp="174wam685qaj9" />
              <p className="text-green-500 text-sm font-mono uppercase tracking-wide" data-id="element-9871" data-mp="pluitkrqfew">
                {success}
              </p>
            </div>}
          {error && <div className="bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 mb-8 flex items-center gap-3" data-id="element-9872" data-mp="27e4e499pswju">
              <AlertTriangle className="text-[#FF0000] flex-shrink-0" size={20} data-id="element-9873" data-mp="2cc5taj40sbq1" />
              <p className="text-[#FF0000] text-sm font-mono uppercase tracking-wide" data-id="element-9874" data-mp="2hb0etstdgedu">
                {error}
              </p>
            </div>}

          {/* Subscription & Billing */}
          <section aria-labelledby="billing-heading" className="mb-8" data-id="element-9875" data-mp="2r0togo9yejfg">
            <div className="mb-5" data-id="element-9876" data-mp="2v2f8quothzzo">
              <h2 id="billing-heading" className="text-xl font-black text-white uppercase tracking-tight" data-id="element-9877" data-mp="2v1lyhjqngscs">
                Subscription & Billing
              </h2>
              <p className="mt-1.5 text-gray-400 font-mono text-xs" data-id="element-9878" data-mp="34za63a9ctucd">
                Manage your ToxicDetector plan, scan access, and billing.
              </p>
            </div>

            <div className="bg-[#0a0a0a] border border-gray-800 p-8 relative group" data-id="element-9879" data-mp="1anvf6q4lxov1">
              {/* Corner Brackets */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9880" data-mp="3cisszrlpy7q8" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9881" data-mp="3mayld2fvur5y" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9882" data-mp="3hcdu3silzhtn" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9883" data-mp="2ongclrm81p1y" />

              <div className="flex items-start gap-4" data-id="element-9884" data-mp="33lil8g8410q2">
                <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center border ${unlimited ? 'bg-[#FFD700]/10 border-[#FFD700] text-[#FFD700]' : planId === 'single_scan' ? 'bg-[#FF0000]/10 border-[#FF0000] text-[#FF0000]' : 'bg-gray-900 border-gray-700 text-gray-500'}`} data-id="element-9885" data-mp="2ykuu7ujvsjvu">
                  {unlimited || planId === 'single_scan' ? <Crown size={24} data-id="element-9886" data-mp="cf1e4ertq25w" /> : <Sparkles size={24} data-id="element-9887" data-mp="gf1qltvd43ab" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-9888" data-mp="144w48dni9vvm">
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500" data-id="element-9889" data-mp="146778b94mzlo">
                    Current plan
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-3" data-id="element-9890" data-mp="vcu4yip6almx">
                    <h3 className="text-2xl font-black text-white uppercase tracking-tight" data-id="element-9891" data-mp="varig8n8bamy">
                      {loadingProfile ? 'Loading…' : planLabel}
                    </h3>
                    {!loadingProfile && <span className={`px-2 py-1 font-mono text-[9px] font-black uppercase tracking-[0.15em] ${statusLabel === 'Active' ? 'bg-green-500/10 border border-green-500/40 text-green-400' : statusLabel === 'Free' ? 'bg-gray-900 border border-gray-700 text-gray-400' : 'bg-[#FF0000]/10 border border-[#FF0000]/40 text-[#FF0000]'}`} data-id="element-9892" data-mp="1ru8dtemqw5yg">
                        {statusLabel}
                      </span>}
                  </div>
                  <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2" data-id="element-9893" data-mp="2x69heiqqx0vt">
                    <div data-id="element-9894" data-mp="2ih3kj1fdrmr3">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-9895" data-mp="2nedbilif46h1">
                        Price
                      </dt>
                      <dd className="mt-1 font-mono text-sm text-white" data-id="element-9896" data-mp="29f452dk0kqq9">
                        {priceLabel}
                      </dd>
                    </div>
                    <div data-id="element-9897" data-mp="1ur3s1k6ilrfk">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-9898" data-mp="1usoq3js1fsi6">
                        Billing
                      </dt>
                      <dd className="mt-1 font-mono text-sm text-white" data-id="element-9899" data-mp="23t39uepcszjk">
                        {billingLabel}
                      </dd>
                    </div>
                    <div data-id="element-9900" data-mp="1yvlbtjcvk4qc">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-9901" data-mp="2dqhs8juu7yei">
                        Scan access
                      </dt>
                      <dd className="mt-1 font-mono text-sm text-white" data-id="element-9902" data-mp="28pkag8dqp6dj">
                        {loadingProfile ? 'Verifying clearance…' : accessLabel}
                      </dd>
                    </div>
                    {renewalDate && <div data-id="element-9903" data-mp="2wgppilbuvums">
                        <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-9904" data-mp="2wi0tom97r4ef">
                          Renews
                        </dt>
                        <dd className="mt-1 font-mono text-sm text-white" data-id="element-9905" data-mp="1p59t1aynnkxt">
                          {renewalDate}
                        </dd>
                      </div>}
                  </dl>
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-4" data-id="element-9906" data-mp="1y3bq5ff9ptft">
                <button onClick={() => setShowSubscriptionModal(true)} className="flex-1 bg-[#FF0000] text-white py-3 font-bold hover:bg-[#D90000] transition-colors duration-200 ease-out text-xs uppercase tracking-widest shadow-[0_0_15px_rgba(255,0,0,0.3)]" data-id="element-9907" data-mp="22wws1ijzf7ka">
                  {unlimited ? 'View plans' : 'Upgrade plan'}
                </button>
                <button onClick={() => setShowBillingModal(true)} className="flex-1 bg-transparent border border-gray-700 text-gray-300 py-3 font-bold hover:border-gray-500 hover:text-white transition-colors duration-200 ease-out text-xs uppercase tracking-widest" data-id="element-9908" data-mp="2ltplbet5g8em">
                  Billing history
                </button>
              </div>
            </div>
          </section>

          {/* Account Information */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-9909" data-mp="a7489q98p47e">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9910" data-mp="59vmlfovui1e" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9911" data-mp="12tyno8r79de0" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9912" data-mp="sxdgbgrpjq70" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9913" data-mp="oymhn57lg0hs" />

            <div className="flex items-center gap-3 mb-6" data-id="element-9914" data-mp="39mizyvreeig7">
              <User size={20} className="text-[#FF0000]" data-id="element-9915" data-mp="39p57wwi7azyy" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-9916" data-mp="11dav7qrld0jq">
                Identity Parameters
              </h3>
            </div>

            <form onSubmit={handleSaveChanges} className="space-y-6" data-id="element-9917" data-mp="rfwsetylca9w">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9918" data-mp="rhpyo85akshd">
                <div data-id="element-9919" data-mp="wg166c846qwf">
                  <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-9920" data-mp="wf9db95wj79u">
                    First Name
                  </label>
                  <input type="text" value={formData.firstName} onChange={e => setFormData({
                  ...formData,
                  firstName: e.target.value
                })} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white transition-all font-mono text-sm" data-id="element-9921" data-mp="1p2m09kfz5h3o" />
                </div>
                <div data-id="element-9922" data-mp="3nd4birht69xq">
                  <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-9923" data-mp="3ndvwl8ds4p9r">
                    Last Name
                  </label>
                  <input type="text" value={formData.lastName} onChange={e => setFormData({
                  ...formData,
                  lastName: e.target.value
                })} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white transition-all font-mono text-sm" data-id="element-9924" data-mp="15wag1dx8p7i" />
                </div>
              </div>

              <div data-id="element-9925" data-mp="2qt3ohd2ubbv0">
                <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-9926" data-mp="2lw4xh3eh3l11">
                  Email Address
                </label>
                <div className="flex gap-3" data-id="element-9927" data-mp="3bk0lg8vx4m70">
                  <div className="flex-1 px-4 py-3 bg-[#050505] border border-gray-800 text-gray-400 font-mono text-sm flex items-center" data-id="element-9928" data-mp="3flccmiufxfhb">
                    <Mail size={14} className="mr-3 text-gray-600" data-id="element-9929" data-mp="3kd47h5kmq7d9" />
                    {user?.email || ''}
                  </div>
                  <button type="button" onClick={() => setShowChangeEmailModal(true)} className="px-6 py-3 bg-gray-900 border border-gray-700 text-white font-bold hover:bg-gray-800 hover:border-gray-500 transition-all text-xs uppercase tracking-widest whitespace-nowrap" data-id="element-9930" data-mp="3pdi31o51noue">
                    Change
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-800" data-id="element-9931" data-mp="gusproalpuqh">
                <button type="submit" disabled={loading} className="w-full bg-white text-black py-3 font-bold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-9932" data-mp="luyjrb4cftbq">
                  {loading ? 'Processing Update...' : <>
                      <Save size={16} data-id="element-9933" data-mp="1rntehuds5q0w" />
                      Save Changes
                    </>}
                </button>
              </div>
            </form>
          </div>

          {/* Notifications */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-9934" data-mp="1ipri5s4zrljy">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9935" data-mp="18pqvpxbtspbj" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9936" data-mp="381tef5cmlfo5" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9937" data-mp="2by608vcu8aaz" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9938" data-mp="2gvo1s0oizn8y" />

            <div className="flex items-center gap-3 mb-6" data-id="element-9939" data-mp="27762jnvw9o7f">
              <Bell size={20} className="text-[#FF0000]" data-id="element-9940" data-mp="275mo82tstn8a" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-9941" data-mp="1t6vawimrg5l7">
                Alert Protocols
              </h3>
            </div>

            <div className="space-y-6" data-id="element-9942" data-mp="1y7srwrw1atl4">
              <div className="flex items-center justify-between" data-id="element-9943" data-mp="1k6zx061tlwhh">
                <div data-id="element-9944" data-mp="1k8t4too66mr2">
                  <p className="font-bold text-white text-sm uppercase tracking-wide" data-id="element-9945" data-mp="1o8bqwnrq93vh">
                    Email Reports
                  </p>
                  <p className="text-xs text-gray-500 font-mono mt-1" data-id="element-9946" data-mp="3em2hfpc0iodj">
                    Receive detailed analysis via secure email
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-9947" data-mp="16yhg6zc5xmjt">
                  <input type="checkbox" checked={emailNotifications} onChange={e => handleNotificationChange('email', e.target.checked)} className="sr-only peer" data-id="element-9948" data-mp="12xnwki5r6twq" />
                  <div className="w-11 h-6 bg-gray-900 border border-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-500 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF0000]/20 peer-checked:border-[#FF0000] peer-checked:after:bg-[#FF0000] peer-checked:after:border-[#FF0000]" data-id="element-9949" data-mp="1qmgtohy437sx"></div>
                </label>
              </div>

              <div className="h-px bg-gray-900" data-id="element-9950" data-mp="cdkzi9wsl9r" />

              <div className="flex items-center justify-between" data-id="element-9951" data-mp="34qy39zc47nu7">
                <div data-id="element-9952" data-mp="34p4vggprmxkm">
                  <p className="font-bold text-white text-sm uppercase tracking-wide" data-id="element-9953" data-mp="2zsmsnmdzx9ax">
                    System Alerts
                  </p>
                  <p className="text-xs text-gray-500 font-mono mt-1" data-id="element-9954" data-mp="3dihzoeta5lm0">
                    Real-time notifications for completed scans
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-9955" data-mp="2lrtzp63cem9x">
                  <input type="checkbox" checked={pushNotifications} onChange={e => handleNotificationChange('push', e.target.checked)} className="sr-only peer" data-id="element-9956" data-mp="2gu24o37eik1i" />
                  <div className="w-11 h-6 bg-gray-900 border border-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-500 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF0000]/20 peer-checked:border-[#FF0000] peer-checked:after:bg-[#FF0000] peer-checked:after:border-[#FF0000]" data-id="element-9957" data-mp="a29rv23x7wuv"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-9958" data-mp="wb85izftgfof">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9959" data-mp="11a1kkwar4eqp" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9960" data-mp="rkrpr8oiwhyw" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9961" data-mp="1f9ax9lzr0qpx" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9962" data-mp="1k2vqz1ub6yjb" />

            <div className="flex items-center gap-3 mb-6" data-id="element-9963" data-mp="1oyzqpcx5i157">
              <Shield size={20} className="text-[#FF0000]" data-id="element-9964" data-mp="1jyv73e0y4qar" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-9965" data-mp="1jxaehty6zhfj">
                Security Protocols
              </h3>
            </div>

            <div className="space-y-4" data-id="element-9966" data-mp="1axx4n065oqua">
              <button onClick={() => setShowTwoFactorModal(true)} className="w-full flex items-center justify-between p-4 bg-[#050505] border border-gray-800 hover:border-gray-600 transition-all group/btn" data-id="element-9967" data-mp="1ayynhqteyhmk">
                <div className="flex items-center gap-4" data-id="element-9968" data-mp="w9khmsp1qibx">
                  <div className="p-2 bg-gray-900 border border-gray-800 group-hover/btn:border-gray-600 transition-colors" data-id="element-9969" data-mp="wb3vkcn8chaj">
                    <Lock size={16} className="text-gray-400" data-id="element-9970" data-mp="n8ma40fefsol" />
                  </div>
                  <span className="font-bold text-gray-300 text-sm uppercase tracking-wide group-hover/btn:text-white" data-id="element-9971" data-mp="nap754eyym2t">
                    Two-Factor Authentication
                  </span>
                </div>
                <ChevronRight size={16} className="text-gray-600 group-hover/btn:text-[#FF0000] transition-colors" data-id="element-9972" data-mp="37wmk1zxahv50" />
              </button>

              <button onClick={() => setShowPrivacyModal(true)} className="w-full flex items-center justify-between p-4 bg-[#050505] border border-gray-800 hover:border-gray-600 transition-all group/btn" data-id="element-9973" data-mp="3ctcykitg4iob">
                <div className="flex items-center gap-4" data-id="element-9974" data-mp="2p53g9qvfyd1x">
                  <div className="p-2 bg-gray-900 border border-gray-800 group-hover/btn:border-gray-600 transition-colors" data-id="element-9975" data-mp="2twv25o3njulq">
                    <Shield size={16} className="text-gray-400" data-id="element-9976" data-mp="1wi90365npzu9" />
                  </div>
                  <span className="font-bold text-gray-300 text-sm uppercase tracking-wide group-hover/btn:text-white" data-id="element-9977" data-mp="21g235qbnydqb">
                    Privacy Configuration
                  </span>
                </div>
                <ChevronRight size={16} className="text-gray-600 group-hover/btn:text-[#FF0000] transition-colors" data-id="element-9978" data-mp="2zjjxgxu5698" />
              </button>
            </div>
          </div>

          {/* Logout Button */}
          <button onClick={handleLogout} className="w-full bg-gray-900 border border-gray-800 text-gray-400 py-4 font-bold hover:bg-gray-800 hover:text-white hover:border-gray-600 transition-all text-xs uppercase tracking-widest mb-8" data-id="element-9979" data-mp="bxlrkfc2qx5h">
            Terminate Session
          </button>

          {/* Danger Zone — always last on the page */}
          <div className="bg-[#FF0000]/5 border border-[#FF0000]/30 p-8 relative" data-id="element-9980" data-mp="vo79hopmahsl">
            <h3 className="font-bold text-[#FF0000] mb-4 flex items-center gap-2 uppercase tracking-wider text-sm" data-id="element-9981" data-mp="31h5mzmx36ht1">
              <AlertTriangle size={16} data-id="element-9982" data-mp="2xglp2h4ova5u" />
              Critical Zone
            </h3>
            <p className="text-xs text-gray-400 mb-6 font-mono" data-id="element-9983" data-mp="2nc5cpxqf4b6s">
              WARNING: Account deletion is irreversible. All forensic data and
              analysis logs will be permanently purged.
            </p>
            <button type="button" onClick={openDeleteAccount} className="w-full bg-[#FF0000] text-white py-3 font-bold hover:bg-[#D90000] transition-colors duration-150 ease-out text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-9984" data-mp="2dksk33wspgzp">
              <Trash2 size={16} data-id="element-9985" data-mp="28jv1wrbscmy7" />
              Delete account
            </button>
          </div>
        </main>
      </div>

      {/* Modals */}
      <ChangeEmailModal isOpen={showChangeEmailModal} onClose={() => setShowChangeEmailModal(false)} data-id="element-9986" data-mp="1zkhqht43poau" />
      <TwoFactorModal isOpen={showTwoFactorModal} onClose={() => setShowTwoFactorModal(false)} data-id="element-9987" data-mp="3p1iqan17y8tb" />
      <PrivacySettingsModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} data-id="element-9988" data-mp="3tyiwqz1zddky" />
      <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} data-id="element-9989" data-mp="3nj202n5ow29i" />
      <DeleteAccountModal isOpen={showDeleteAccountModal} onClose={() => {
      if (deleteLoading) return;
      setShowDeleteAccountModal(false);
      setDeleteError(null);
    }} onConfirm={handleDeleteAccount} loading={deleteLoading} error={deleteError} data-id="element-9990" data-mp="3ijfpklh8x04e" />
      <BillingManagementModal isOpen={showBillingModal} onClose={() => setShowBillingModal(false)} data-id="element-9991" data-mp="a5gsfgijf8pb" />
      <CancelSubscriptionModal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} currentPlan={planId} data-id="element-9992" data-mp="5bkzish4sc10" />
    </div>;
}